import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Pagination,
  Stack,
  Tooltip,
  Collapse,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  Edit,
  Visibility,
  DeleteOutline,
  Inventory,
  Error,
  AccessTime,
  CalendarToday,
  EventRepeat,
  Science,
  Route,
  Handshake,
  Gavel,
  Notes as NotesIcon,
  History,
  KeyboardArrowUp,
  Tune,
} from "@mui/icons-material";
import { formatDateField } from "../../../../../base/FormatTime";
import RefillHistoryTable from "./Table/RefillHistoryTable";

const InfoRow = ({ icon, label, value, color = "#475569" }) => {
  if (!value) return null;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
      }}
    >
      <Box
        sx={{
          color,
          display: "flex",
          alignItems: "center",
        }}
      >
        {icon}
      </Box>

      <Typography
        sx={{
          fontSize: "0.68rem",
          color: "#64748b",
          fontWeight: 500,
        }}
      >
        {label}:
      </Typography>

      <Typography
        sx={{
          fontSize: "0.68rem",
          color: "#334155",
          fontWeight: 600,
        }}
      >
        {value}
      </Typography>
    </Box>
  );
};

const MedicationsTable = ({
  medications,
  isLoading,
  currentPage,
  totalPages,
  onPageChange,
  onRefill,
  onAdjust,
  onView,
  onEdit,
  onDelete,
  isAdmin,
}) => {
  const [expandedMedicine, setExpandedMedicine] = useState(null);

  const handleHistoryToggle = (medicineId) => {
    setExpandedMedicine((current) =>
      current === medicineId ? null : medicineId
    );
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          py: 5,
        }}
      >
        Loading...
      </Box>
    );
  }

  return (
    <>
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          borderRadius: "10px",
          border: "1px solid #e2e8f0",
          overflow: "hidden",
        }}
      >
        <Table size="small">
          <TableHead sx={{ bgcolor: "#f1f5f9" }}>
            <TableRow>
              {[
                "TYPE",
                "MEDICATION DETAILS & STOCK",
                "SCHEDULE",
                "CLINICAL",
                "START DATE",
                "ACTIONS",
              ].map((h, i) => (
                <TableCell
                  key={h}
                  align={i === 5 ? "right" : "left"}
                  sx={{
                    fontWeight: 700,
                    color: "#475569",
                    fontSize: "0.68rem",
                    letterSpacing: 0.3,
                  }}
                >
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {medications?.length > 0 ? (
              medications.map((med) => {
                const historyOpen = expandedMedicine === med.id;

                return (
                  <React.Fragment key={med.id}>
                    <TableRow
                      hover
                      sx={{
                        "& td": {
                          verticalAlign: "top",
                          py: 1.75,
                        },
                      }}
                    >
                      <TableCell>
                        <Stack spacing={0.5} alignItems="flex-start">
                          <Chip
                            label={med.medicine_type}
                            size="small"
                            sx={{
                              fontWeight: 600,
                              borderRadius: "4px",
                              fontSize: "0.65rem",
                              height: 20,
                              bgcolor:
                                med.medicine_type === "Blister Pack"
                                  ? "#fef3c7"
                                  : "#dcfce7",
                            }}
                          />

                          {med.medication_type && (
                            <Chip
                              label={med.medication_type}
                              size="small"
                              variant="outlined"
                              sx={{
                                fontWeight: 500,
                                borderRadius: "4px",
                                fontSize: "0.6rem",
                                height: 18,
                              }}
                            />
                          )}

                          {med.controlled_medication && (
                            <Chip
                              label="Controlled"
                              size="small"
                              color="error"
                              sx={{
                                height: 18,
                                fontSize: "0.55rem",
                                fontWeight: 700,
                              }}
                            />
                          )}
                        </Stack>
                      </TableCell>

                      <TableCell sx={{ width: "35%" }}>
                        {med.medicine_type === "Blister Pack" ? (
                          <Box>
                            <Typography
                              sx={{
                                fontSize: "0.78rem",
                                fontWeight: 700,
                                color: "#1e293b",
                                mb: 0.25,
                              }}
                            >
                              {med.title}
                            </Typography>

                            <Typography
                              sx={{
                                fontSize: "0.68rem",
                                color: "#64748b",
                                mb: 0.75,
                                display: "block",
                              }}
                            >
                              {med.medicine_lists?.map((m) => m.name).join(", ")}
                            </Typography>

                            <Stack direction="row" flexWrap="wrap" gap={0.75}>
                              <Box
                                sx={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                  px: 0.75,
                                  py: 0.4,
                                  borderRadius: "6px",
                                  bgcolor:
                                    Number(med.total_slots_remaining) < 5
                                      ? "#fee2e2"
                                      : "#f0fdf4",
                                }}
                              >
                                {Number(med.total_slots_remaining) < 5 ? (
                                  <Error
                                    sx={{
                                      fontSize: 13,
                                      color: "#ef4444",
                                    }}
                                  />
                                ) : (
                                  <Inventory
                                    sx={{
                                      fontSize: 13,
                                      color: "#16a34a",
                                    }}
                                  />
                                )}

                                <Typography
                                  sx={{
                                    fontSize: "0.65rem",
                                    fontWeight: 700,
                                    color:
                                      Number(med.total_slots_remaining) < 5
                                        ? "#ef4444"
                                        : "#16a34a",
                                  }}
                                >
                                  Stock: {med.total_slots_remaining} Slots
                                </Typography>
                              </Box>

                              {med.number_of_cards && (
                                <Box
                                  sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                    px: 0.75,
                                    py: 0.4,
                                    borderRadius: "6px",
                                    bgcolor: "#eff6ff",
                                  }}
                                >
                                  <Inventory
                                    sx={{
                                      fontSize: 13,
                                      color: "#2469F0",
                                    }}
                                  />

                                  <Typography
                                    sx={{
                                      fontSize: "0.65rem",
                                      fontWeight: 700,
                                      color: "#2469F0",
                                    }}
                                  >
                                    {med.number_of_cards} Cards
                                  </Typography>
                                </Box>
                              )}
                            </Stack>

                            {med.notes && (
                              <Box
                                sx={{
                                  mt: 0.75,
                                  display: "flex",
                                  alignItems: "flex-start",
                                  gap: 0.5,
                                }}
                              >
                                <NotesIcon
                                  sx={{
                                    fontSize: 13,
                                    color: "#94a3b8",
                                    mt: 0.2,
                                  }}
                                />

                                <Typography
                                  sx={{
                                    fontSize: "0.65rem",
                                    color: "#64748b",
                                    fontStyle: "italic",
                                  }}
                                >
                                  {med.notes}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        ) : (
                          <Stack spacing={0.75}>
                            {med.individual_meds?.map((item, idx) => (
                              <Box
                                key={idx}
                                sx={{
                                  p: 1,
                                  borderRadius: "6px",
                                  border: "1px solid #f1f5f9",
                                  bgcolor:
                                    Number(item.remaining_stock) < 5
                                      ? "#fff5f5"
                                      : "#ffffff",
                                }}
                              >
                                <Stack
                                  direction="row"
                                  justifyContent="space-between"
                                  alignItems="center"
                                >
                                  <Box>
                                    <Typography
                                      sx={{
                                        fontSize: "0.75rem",
                                        fontWeight: 700,
                                        color: "#334155",
                                      }}
                                    >
                                      {item.name}
                                    </Typography>

                                    <Typography
                                      sx={{
                                        fontSize: "0.65rem",
                                        color: "#94a3b8",
                                      }}
                                    >
                                      Dose: {item.dose} mg
                                    </Typography>
                                  </Box>

                                  <Typography
                                    sx={{
                                      fontSize: "0.65rem",
                                      fontWeight: 700,
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 0.4,
                                      color:
                                        Number(item.remaining_stock) < 5
                                          ? "#ef4444"
                                          : "#059669",
                                    }}
                                  >
                                    {Number(item.remaining_stock) < 5 && (
                                      <Error sx={{ fontSize: 12 }} />
                                    )}

                                    {item.remaining_stock} Qty
                                  </Typography>
                                </Stack>
                              </Box>
                            ))}
                          </Stack>
                        )}
                      </TableCell>

                      <TableCell>
                        <Stack spacing={0.6}>
                          <InfoRow
                            icon={<EventRepeat sx={{ fontSize: 13 }} />}
                            label="Frequency"
                            value={med.frequency}
                            color="#2469F0"
                          />

                          {med.time_data?.length > 0 && (
                            <InfoRow
                              icon={<AccessTime sx={{ fontSize: 13 }} />}
                              label="Time"
                              value={med.time_data
                                .map((t) => t.time?.slice(0, 5))
                                .join(", ")}
                              color="#059669"
                            />
                          )}

                          {med.every && (
                            <InfoRow
                              icon={<EventRepeat sx={{ fontSize: 13 }} />}
                              label="Every"
                              value={`${med.every} day(s)`}
                              color="#f59e0b"
                            />
                          )}

                          {med.weekdays?.length > 0 && (
                            <Box>
                              <Typography
                                sx={{
                                  fontSize: "0.6rem",
                                  color: "#94a3b8",
                                  fontWeight: 600,
                                  mb: 0.4,
                                }}
                              >
                                Days:
                              </Typography>

                              <Stack direction="row" flexWrap="wrap" gap={0.4}>
                                {med.weekdays.map((day) => (
                                  <Chip
                                    key={day}
                                    label={day}
                                    size="small"
                                    sx={{
                                      height: 16,
                                      fontSize: "0.55rem",
                                      fontWeight: 600,
                                      bgcolor: "#dbeafe",
                                      color: "#1e40af",
                                      borderRadius: "3px",
                                    }}
                                  />
                                ))}
                              </Stack>
                            </Box>
                          )}
                        </Stack>
                      </TableCell>

                      <TableCell>
                        <Stack spacing={0.6}>
                          {med.route_administration && (
                            <InfoRow
                              icon={<Route sx={{ fontSize: 13 }} />}
                              label="Route"
                              value={med.route_administration}
                              color="#8b5cf6"
                            />
                          )}

                          {med.how_assisted && (
                            <InfoRow
                              icon={<Handshake sx={{ fontSize: 13 }} />}
                              label="Assist"
                              value={med.how_assisted}
                              color="#0891b2"
                            />
                          )}

                          {med.reason_for_medication && (
                            <InfoRow
                              icon={<Science sx={{ fontSize: 13 }} />}
                              label="Reason"
                              value={med.reason_for_medication}
                              color="#dc2626"
                            />
                          )}

                          {med.dose && (
                            <InfoRow
                              icon={<Gavel sx={{ fontSize: 13 }} />}
                              label="Dose"
                              value={med.dose}
                              color="#7c3aed"
                            />
                          )}

                          {med.care_plan?.length > 0 && (
                            <Box>
                              <Typography
                                sx={{
                                  fontSize: "0.6rem",
                                  color: "#94a3b8",
                                  fontWeight: 600,
                                  mb: 0.4,
                                }}
                              >
                                Care Plans:
                              </Typography>

                              <Stack direction="row" flexWrap="wrap" gap={0.4}>
                                {med.care_plan.map((cp) => (
                                  <Chip
                                    key={cp.id}
                                    label={cp.name}
                                    size="small"
                                    sx={{
                                      height: 16,
                                      fontSize: "0.55rem",
                                      fontWeight: 600,
                                      bgcolor: "#f3e8ff",
                                      color: "#6b21a8",
                                      borderRadius: "3px",
                                    }}
                                  />
                                ))}
                              </Stack>
                            </Box>
                          )}
                        </Stack>
                      </TableCell>

                      <TableCell>
                        <Stack spacing={0.4}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <CalendarToday
                              sx={{
                                fontSize: 13,
                                color: "#059669",
                              }}
                            />

                            <Typography
                              sx={{
                                fontSize: "0.72rem",
                                color: "#334155",
                                fontWeight: 600,
                              }}
                            >
                              {formatDateField(med.start_date)}
                            </Typography>
                          </Box>

                          {med.end_date && (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <CalendarToday
                                sx={{
                                  fontSize: 13,
                                  color: "#ef4444",
                                }}
                              />

                              <Typography
                                sx={{
                                  fontSize: "0.68rem",
                                  color: "#64748b",
                                  fontWeight: 500,
                                }}
                              >
                                Ends: {formatDateField(med.end_date)}
                              </Typography>
                            </Box>
                          )}

                          {med.expiry_date && (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <Error
                                sx={{
                                  fontSize: 13,
                                  color: "#f59e0b",
                                }}
                              />

                              <Typography
                                sx={{
                                  fontSize: "0.68rem",
                                  color: "#64748b",
                                  fontWeight: 500,
                                }}
                              >
                                Expires: {formatDateField(med.expiry_date)}
                              </Typography>
                            </Box>
                          )}
                        </Stack>
                      </TableCell>

                      <TableCell align="right">
                        <Stack spacing={0.75} alignItems="flex-end">
                          <Stack direction="row" spacing={0.5}>
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => onRefill(med)}
                              sx={{
                                bgcolor: "#059669",
                                fontWeight: 600,
                                fontSize: "0.6rem",
                                textTransform: "none",
                                py: 0.4,
                                px: 1.5,
                                borderRadius: "6px",
                                boxShadow: "none",
                                "&:hover": {
                                  bgcolor: "#047857",
                                  boxShadow: "none",
                                },
                              }}
                            >
                              REFILL
                            </Button>

                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => onAdjust(med)}
                              sx={{
                                color: "#F59E0B",
                                borderColor: alpha("#F59E0B", 0.5),
                                fontWeight: 600,
                                fontSize: "0.6rem",
                                textTransform: "none",
                                py: 0.4,
                                px: 1.5,
                                borderRadius: "6px",
                                "&:hover": {
                                  borderColor: "#F59E0B",
                                  bgcolor: alpha("#F59E0B", 0.08),
                                },
                              }}
                            >
                              ADJUST
                            </Button>
                          </Stack>

                          <Stack direction="row" spacing={0.5}>
                            <Tooltip title="Refill History">
                              <IconButton
                                size="small"
                                onClick={() => handleHistoryToggle(med.id)}
                                sx={{
                                  bgcolor: historyOpen ? "#dbeafe" : "#f1f5f9",
                                  color: historyOpen ? "#2469F0" : "#64748b",
                                  p: 0.5,
                                  "&:hover": {
                                    bgcolor: "#dbeafe",
                                    color: "#2469F0",
                                  },
                                }}
                              >
                                {historyOpen ? (
                                  <KeyboardArrowUp sx={{ fontSize: 16 }} />
                                ) : (
                                  <History sx={{ fontSize: 14 }} />
                                )}
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="View Details">
                              <IconButton
                                size="small"
                                onClick={() => onView(med.id)}
                                sx={{
                                  bgcolor: "#f1f5f9",
                                  p: 0.5,
                                }}
                              >
                                <Visibility sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={() => onEdit(med.id)}
                                sx={{ p: 0.5 }}
                              >
                                <Edit sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Tooltip>

                            {isAdmin && (
                              <Tooltip title="Delete">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => onDelete(med.id)}
                                  sx={{ p: 0.5 }}
                                >
                                  <DeleteOutline sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Stack>
                        </Stack>
                      </TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell
                        colSpan={6}
                        sx={{
                          p: 0,
                          borderBottom: historyOpen
                            ? "1px solid #e2e8f0"
                            : "none",
                        }}
                      >
                        <Collapse in={historyOpen} timeout="auto" unmountOnExit>
                          <RefillHistoryTable medicineId={med.id} />
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })
            ) : (
              <TableRow>
                <TableCell
                  colSpan={6}
                  sx={{
                    textAlign: "center",
                    py: 5,
                    color: "#94a3b8",
                    fontSize: "0.8rem",
                  }}
                >
                  No medications found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          mt: 3,
        }}
      >
        <Pagination
          count={totalPages}
          page={currentPage}
          onChange={(e, p) => onPageChange(p)}
          color="primary"
          shape="rounded"
          size="small"
        />
      </Box>
    </>
  );
};

export default MedicationsTable;